import { useState, useEffect, useCallback } from 'react';
import type { PlaycastGamepadId } from '@playcastdotio/CoreTypes';
import type { PeerCoordinator } from '../PeerCoordinator';
import type { PeerPermissions } from '../types';

export type ConnectedPlayerInfo = {
  peerId: string;
  playerName: string;
  permissions: PeerPermissions;
  paused: boolean;
  afk: boolean;
  gamepadSlots: PlaycastGamepadId[];
  stats?: { rtt: number; connectionType: string };
};

function buildPlayerList(coordinator: PeerCoordinator): ConnectedPlayerInfo[] {
  const peers = coordinator.getPeersByType('player');
  return peers.map((peer) => {
    const stats = coordinator.getPeerStats(peer.peerId);
    return {
      peerId: peer.peerId,
      playerName: peer.metadata.displayName ?? 'Player',
      permissions: peer.permissions,
      paused: coordinator.isPeerPaused(peer.peerId),
      afk: coordinator.isPlayerAfk(peer.peerId),
      gamepadSlots: coordinator.getGamepadSlots(peer.peerId),
      stats: stats ? { rtt: stats.roundTripTime, connectionType: stats.connectionType } : undefined,
    };
  });
}

/**
 * Reactive hook for connected players with enriched state.
 * Re-fetches on any join/leave/permissions/pause/afk/gamepad event.
 */
export function useConnectedPlayers(coordinator: PeerCoordinator): ConnectedPlayerInfo[] {
  const [players, setPlayers] = useState<ConnectedPlayerInfo[]>(() => buildPlayerList(coordinator));

  const refresh = useCallback(() => {
    setPlayers(buildPlayerList(coordinator));
  }, [coordinator]);

  useEffect(() => {
    const unsubs = [
      coordinator.events.onPlayerJoined.addListener(refresh),
      coordinator.events.onPlayerLeft.addListener(refresh),
      coordinator.events.permissionsChanged.addListener(refresh),
      coordinator.events.pauseStateChanged.addListener(refresh),
      coordinator.events.afkStateChanged.addListener(refresh),
      coordinator.events.gamepadSlotsChanged.addListener(refresh),
    ];
    return () => unsubs.forEach((u) => u());
  }, [coordinator, refresh]);

  return players;
}
